import { useMemo, useState } from 'react';
import { ethereumAddressToAccountId } from '@chainflip/delegation';
import { capitalize } from '@chainflip/utils/string';
import classNames from 'classnames';
import { isHexString } from 'ethers';
import { DashboardModalWithCloseButton, ModalContent } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import useRpcRequest from '@/shared/hooks/useRpcRequest';
import useToast from '@/shared/hooks/useToast';
import useOperatorAccount from '../../../hooks/useOperatorAccount';
import { useExtrinsicMutation } from '../../../hooks/useStateChainAccount';

const AccountIdView = ({
  address,
  setAddress,
  onSuccess,
  action,
  color,
}: {
  address: string;
  setAddress: (address: string) => void;
  onSuccess: () => void;
  action: 'allow' | 'block';
  color: 'black' | 'white';
}) => {
  const [error, setError] = useState<string>();
  const accountId = useMemo(() => {
    if (!isHexString(address)) return undefined;

    try {
      return ethereumAddressToAccountId(address);
    } catch {
      return undefined;
    }
  }, [address]);

  const { data: accountExists = false } = useRpcRequest({
    method: 'cf_accounts',
    args: [],
    select(result) {
      return (accountId && result.find(([idSs58]) => idSs58 === accountId)) !== undefined;
    },
  });

  const toast = useToast('add-to-list');
  const { mutate: addToList, isPending } = useExtrinsicMutation(`operator_${action}Delegator`, {
    onMutate() {
      toast.loading('Submitting transaction to the State Chain...');
    },
    onSuccess() {
      toast.success(`Successfully added delegator to the ${color}list`);
      onSuccess();
    },
    onError(err) {
      toast.error('Error while submitting transaction:', {
        description: err.message,
      });
    },
  });

  return (
    <ModalContent
      className="w-[460px]"
      title={`Add Delegator to ${capitalize(color)}list`}
      buttons={
        <Button
          size="medium"
          type="primary-standard"
          loading={isPending && 'iconOnly'}
          disabled="ifLoading"
          onClick={() => {
            setError(undefined);
            if (!accountId) {
              setError('Please provide a valid Ethereum address');
              return;
            }
            if (!accountExists) {
              toast.error('This account does not exist on Chainflip');
              return;
            }

            addToList(accountId);
          }}
        >
          {`Add to ${color}list`}
        </Button>
      }
      description={`Please enter the Ethereum address of the Delegator you want to add to the ${color}list.`}
    >
      <div className="flex flex-col gap-y-6">
        <div className="relative flex flex-col">
          <input
            placeholder="0x..."
            type="text"
            className={classNames(
              'rounded-md border border-cf-gray-4 bg-cf-gray-2 px-3 py-2 text-14 text-white outline-none transition placeholder:text-cf-light-2 hover:bg-cf-gray-3 disabled:cursor-not-allowed disabled:hover:bg-cf-gray-2',
              error && 'border-cf-red-1',
            )}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            spellCheck={false}
          />
          {error && (
            <span className="absolute -bottom-6 text-12 text-cf-red-1">
              {error ?? 'Enter a valid account ID'}
            </span>
          )}
        </div>
      </div>
    </ModalContent>
  );
};

export default function AddToListModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [address, setAddress] = useState('');
  const [success, setSuccess] = useState(false);
  const operator = useOperatorAccount();
  const action = operator?.openDelegationEnabled ? 'block' : 'allow';
  const color = action === 'block' ? 'black' : 'white';

  const reset = () => {
    setAddress('');
    setSuccess(false);
  };

  let children;

  if (success) {
    children = (
      <ModalContent
        title={`Delegator ${capitalize(color)}listed`}
        success
        buttons={
          <Button size="medium" fullWidth type="primary-standard" onClick={onClose}>
            Return to Dashboard
          </Button>
        }
      >
        {null}
      </ModalContent>
    );
  } else {
    children = (
      <AccountIdView
        address={address}
        setAddress={setAddress}
        onSuccess={() => setSuccess(true)}
        action={action}
        color={color}
      />
    );
  }

  return (
    <DashboardModalWithCloseButton open={open} onClose={onClose} afterLeave={reset}>
      {children}
    </DashboardModalWithCloseButton>
  );
}
